import { useEffect, useLayoutEffect, useState } from "react";

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/*******************
@purpose : Below $breakpoint-lg, .results-filters scrolls its chips
           horizontally (see _search-results.scss), which - as a side effect
           of setting overflow-x - also clips overflow-y, cutting off any
           dropdown panel absolutely positioned relative to its own trigger
           button. The design wants the opened panel to instead span the
           full row's width and overlay the results below it, unclipped.

           This measures .results-filters' own box - not the individual
           trigger's - each time a panel opens, and returns inline style for a
           position:fixed panel spanning that same left/width. Escaping the
           row's clipping needs more than position:fixed on its own (iOS
           Safari clips fixed descendants of a touch scroller against the
           scroller), so FilterDropdownPanel portals the panel out to
           document.body whenever this returns a style. Desktop (isOpen but
           viewport >= breakpoint-lg) gets null, leaving the existing
           position:absolute CSS anchored to the trigger untouched.

           Runs as a layout effect so the panel is positioned before its
           first paint rather than flashing in the wrong place for a frame.
@Parameter : {isOpen, containerRef} - containerRef is the .filter-dropdown
             trigger wrapper; its closest .results-filters ancestor is what
             gets measured
@Author : INIC
******************/
export const useMobilePanelOverlay = (isOpen, containerRef) => {
  const [overlayStyle, setOverlayStyle] = useState(null);

  useIsomorphicLayoutEffect(() => {
    if (!isOpen) {
      setOverlayStyle(null);
      return;
    }

    const isMobile = window.matchMedia("(max-width: 991.8px)").matches;
    const row = containerRef.current?.closest(".results-filters");
    if (!isMobile || !row) {
      setOverlayStyle(null);
      return;
    }

    const measure = () => {
      const rect = row.getBoundingClientRect();
      setOverlayStyle({ top: rect.bottom, left: rect.left, width: rect.width });
    };

    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);

    document.body.classList.add("model-active");
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
      document.body.classList.remove("model-active");
    };
  }, [isOpen, containerRef]);

  return overlayStyle;
};
